import React, { useState } from 'react';
import { Container, Typography, Box, Button, Card, CardContent, FormControlLabel, Switch } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UniversalMap from '../components/maps/UniversalMap';
import { useMapProvider } from '../components/maps/MapProvider';

const UniversalMapTestPage: React.FC = () => {
  const navigate = useNavigate();
  const { provider } = useMapProvider();
  const [center] = useState({ lat: 39.8282, lng: -98.5795 }); // Center of USA
  const [zoom] = useState(4);
  const [showSearch, setShowSearch] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [interactive, setInteractive] = useState(true);

  // Sample markers for testing
  const [markers] = useState([
    { position: { lat: 40.7128, lng: -74.0060 }, title: 'New York City' },
    { position: { lat: 34.0522, lng: -118.2437 }, title: 'Los Angeles' },
  ]);

  // Sample entries for testing
  const [entries] = useState([
    {
      id: 1,
      title: 'Visit to Central Park',
      location: { lat: 40.7829, lng: -73.9654 },
      locationName: 'Central Park, NY',
      content: 'Had a wonderful walk through Central Park. The fall colors were amazing!',
      date: '2024-10-01'
    },
    {
      id: 2,
      title: 'Golden Gate Bridge',
      location: { lat: 37.8199, lng: -122.4783 },
      locationName: 'Golden Gate Bridge, SF',
      content: 'Iconic bridge with stunning views of San Francisco Bay.',
      date: '2024-09-15'
    }
  ]);

  const handleMapClick = (location: { lat: number; lng: number }) => {
    console.log('Map clicked at:', location);
  };

  const handleLocationSearch = (location: { lat: number; lng: number }, locationName: string) => {
    console.log('Location searched:', location, locationName);
  };

  const handleMarkerClick = (entry: any) => {
    console.log('Marker clicked:', entry);
    alert(`Clicked on: ${entry.title}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Universal Map Component Test
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          This page tests the Universal Map Component that automatically switches between 
          Google Maps and Mapbox based on admin settings.
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          ← Back
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Settings
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Active Provider:</strong> {provider.charAt(0).toUpperCase() + provider.slice(1)}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showSearch}
                  onChange={(e) => setShowSearch(e.target.checked)}
                />
              }
              label="Show Search"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showControls}
                  onChange={(e) => setShowControls(e.target.checked)}
                />
              }
              label="Show Controls"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={interactive}
                  onChange={(e) => setInteractive(e.target.checked)}
                />
              }
              label="Interactive"
            />
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Universal Map Display
          </Typography>
          <Box sx={{ height: 500, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <UniversalMap
              center={center}
              zoom={zoom}
              markers={markers}
              entries={entries}
              showSearch={showSearch}
              showControls={showControls}
              interactive={interactive}
              onMapClick={handleMapClick}
              onLocationSearch={handleLocationSearch}
              onMarkerClick={handleMarkerClick}
              style={{ height: '100%' }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Test Features:
        </Typography>
        <Typography component="div" variant="body2">
          <ul>
            <li>✓ Automatic provider switching (change in Admin Panel → Map Settings)</li>
            <li>✓ Sample markers (red pins - New York, Los Angeles)</li>
            <li>✓ Sample entries (blue pins - Central Park, Golden Gate Bridge)</li>
            <li>✓ Click handlers (check browser console)</li>
            <li>✓ Search functionality (type location name)</li>
            <li>✓ Current location button (GPS icon)</li>
            <li>✓ Toggleable UI options</li>
          </ul>
        </Typography>
      </Box>
    </Container>
  );
};

export default UniversalMapTestPage;